import AppContainer from "../Structure/AppContainer";
import React, { useContext, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import AuthContext from "../../Context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import ajaxUser from "../../util/remote/ajaxUser";
import dictionary from "../../util/dictionary";

const ViewUserPage = () => {
  const { userId } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [usersList, setUsersList] = useState([]);
  const [viewType, setViewType] = useState("table"); // "table" or "card"

  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await ajaxUser.fetchUserList({ page: 1 });
      if (response.status === "OK") {
        setUsersList(response.details || []);
      } else {
        toast.error(response.message || "Failed to fetch users");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while fetching users");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Toggle view
  const toggleView = (type) => setViewType(type);

  return (
    <AppContainer title="System Users">
      <div className="container-fluid">
        <Toaster />

        <div className="row mb-3">
          <div className="col-lg-3">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate("/users/register_newbie")}
            >
              Register User
            </button>
          </div>

          <div className="col-lg-9 text-end">
            <button
              className={`btn btn-sm me-2 ${
                viewType === "table" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => toggleView("table")}
            >
              Table View
            </button>
            <button
              className={`btn btn-sm ${
                viewType === "card" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => toggleView("card")}
            >
              Card View
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center my-5">Loading users...</div>
        ) : usersList.length === 0 ? (
          <div className="text-center my-5">No users found</div>
        ) : viewType === "table" ? (
          // TABLE VIEW
          <div className="row">
            <div className="col-lg-12">
              <div className="card custom-card pb-0">
                <table className="table table-striped table-hover table-responsive">
                  <thead
                    style={{ backgroundColor: "#022151ff", color: "white" }}
                  >
                    <tr>
                      <th style={{ color: "white" }}>#</th>
                      <th style={{ color: "white" }}>Avatar & Name</th>
                      <th style={{ color: "white" }}>Username</th>
                      <th style={{ color: "white" }}>Email</th>
                      <th style={{ color: "white" }}>Contact</th>
                      <th style={{ color: "white" }}>Role</th>
                      <th style={{ color: "white" }}>View Permissions</th>
                      <th style={{ color: "white" }}>Status</th>
                      <th style={{ color: "white" }}>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {usersList.map((user, index) => (
                      <tr key={user.id}>
                        <td>{index + 1}</td>
                        <td className="d-flex align-items-center">
                          <img
                            alt="avatar"
                            className="rounded-circle avatar-md me-2"
                            src={`${dictionary.apiHost}${user.photo}`}
                          />
                          {user.first_name} {user.last_name}
                        </td>
                        <td>{user.username}</td>
                        <td>{user.email}</td>
                        <td>{user.contact}</td>
                        <td>{user.role?.role_name || "N/A"}</td>

                        <td className="text-center">
                          <Link
                            to={`/user/permissions/${user.id}`}
                            className="btn btn-sm btn-outline-success"
                            title="View User Permissions"
                          >
                            <i className="fe fe-eye"></i> User permissions
                          </Link>
                        </td>
                        <td className="text-center">
                          {user.is_active === "1" ? (
                            <span className="label text-success d-flex">
                              <span className="dot-label bg-success me-1"></span>
                              Active
                            </span>
                          ) : (
                            <span className="label text-danger d-flex">
                              <span className="dot-label bg-danger me-1"></span>
                              Inactive
                            </span>
                          )}
                        </td>
                        <td>
                          <button className="btn btn-sm btn-primary me-1">
                            <i className="fe fe-search"></i>
                          </button>
                          <button className="btn btn-sm btn-info me-1">
                            <i className="fe fe-edit-2"></i>
                          </button>
                          <button className="btn btn-sm btn-danger">
                            <i className="fe fe-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          // CARD VIEW
          // CARD VIEW
          <div className="row">
            {usersList.map((user) => (
              <div className="col-md-4 col-lg-4 mb-3" key={user.id}>
                <div
                  className="card h-100"
                  style={{
                    borderRadius: "12px",
                    boxShadow: "4px 4px 12px rgba(0,0,0,0.1)",
                    transition: "transform 0.2s, box-shadow 0.3s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.boxShadow =
                      "6px 6px 18px rgba(2, 33, 81, 0.25)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.boxShadow =
                      "4px 4px 12px rgba(0,0,0,0.1)")
                  }
                >
                  <div className="card-body text-center">
                    {/* Avatar */}
                    <img
                      alt="avatar"
                      className="rounded-circle mb-3"
                      style={{
                        width: "80px",
                        height: "80px",
                        objectFit: "cover",
                      }}
                      src={`${dictionary.apiHost}${user.photo}`}
                    />

                    {/* Name & Role */}
                    <h6 className="mb-1">
                      {user.first_name} {user.last_name}
                    </h6>
                    <span className="badge bg-primary mb-2">
                      {user.role?.role_name || "N/A"}
                    </span>

                    {/* Username, Email, Contact */}
                    <p className="text-muted mb-1">@{user.username}</p>
                    <small className="d-block text-muted">{user.email}</small>
                    <small className="d-block text-muted">{user.contact}</small>

                    {/* Status */}
                    <div className="mt-2">
                      <span
                        className={`badge px-3 py-1 ${
                          user.is_active === "1" ? "bg-success" : "bg-danger"
                        }`}
                      >
                        {user.is_active === "1" ? "Active" : "Inactive"}
                      </span>
                    </div>

                    {/* Permissions link */}
                    <div className="mt-3">
                      <Link
                        to={`/user/permissions/${user.id}`}
                        className="btn btn-sm btn-outline-success w-50"
                      >
                        <i className="fe fe-eye"></i> Permissions
                      </Link>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-center mt-3">
                      <button className="btn btn-sm btn-primary me-2">
                        <i className="fe fe-search"></i>
                      </button>
                      <button className="btn btn-sm btn-info me-2">
                        <i className="fe fe-edit-2"></i>
                      </button>
                      <button className="btn btn-sm btn-danger">
                        <i className="fe fe-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppContainer>
  );
};

export default ViewUserPage;
